import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import api from "../auth/api";
import { alertSuccess, alertError } from "../components/Alert";

// Fetch Panel User Data
export const fetchPanelUser = createAsyncThunk(
  "panelUser/fetchPanelUser",
  async ({ page = 1, search = "", fetchAll = false }, { rejectWithValue, getState }) => {
    try {
      const { panelUser } = getState();
      const { pageSize , cache} = panelUser;

        const cacheKey = `${page}_${search}_${pageSize}_${fetchAll}`;

      // ✅ cache check
      if (cache[cacheKey]) {
        return {
          data: cache[cacheKey],
          page,
          search,
          pageSize,
          fromCache: true,
        };
      }



      let url = `/PanelHome/panel/panelUser`;

      if (!fetchAll) {
        url += `?page=${page}&page_size=${pageSize}`;
      } else {
        url += `?page=1&page_size=10000`; // huge number to get all users
      }

      if (search) {
        url += `&search=${search}`;
      }

      const response = await api.get(url);

      return {
        data: response.data,
        page,
        search,
        pageSize,
        fromCache: false,
      };
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

// Add Panel User
export const addPanelUser = createAsyncThunk(
  "panelUser/addPanelUser",
  async (newUserData, { rejectWithValue }) => {
    try {
      const response = await api.post("/PanelHome/panel/panelUser", newUserData);
      alertSuccess(response.data.success);
      return response.data;
    } catch (error) {
      alertError(error.response?.data.errors);
      return rejectWithValue(error.response?.data.errors || error.message);
    }
  }
);

// Update Panel User
export const updatePanelUser = createAsyncThunk(
  "panelUser/updatePanelUser",
  async (userData, { rejectWithValue }) => {
    try {
      const response = await api.patch("/PanelHome/panel/panelUser", userData);
      alertSuccess(response.data.success);
      return response.data;
    } catch (error) {
      alertError(error.response?.data.errors);
      return rejectWithValue(error.response?.data.errors || error.message);
    }
  }
);

// Panel User Slice
const panelUserSlice = createSlice({
  name: "panelUser",
  initialState: {
    panelUserList: { result: [], pagination: { total_pages: 1 } },
    search: "", // ✅ NEW
    currentPage: 1,
      pageSize: 10, // ✅ controlled here
    totalPages: 1,
    cache: {}, // store data per page
    status: "idle", // idle | loading | succeeded | failed
    error: null,
  },
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
     setPage: (state, action) => {
      state.currentPage = action.payload;
    },

      setPageSize: (state, action) => {   // ✅ ADD THIS
      state.pageSize = action.payload;
      state.currentPage = 1;           // ✅ reset page
      state.cache = {};                // ✅ clear cache (VERY IMPORTANT)
    },
  
    // ✅ ADD THIS
  clearCache: (state) => {
    state.cache = {};
  }

  },
  extraReducers: (builder) => {
    builder
      // Fetch Panel User Data
      .addCase(fetchPanelUser.pending, (state) => {
        state.status = "loading";
        state.error = null;
      })
      .addCase(fetchPanelUser.fulfilled, (state, action) => {
      
        const { data, page, search, pageSize, fromCache } = action.payload;

         const cacheKey = `${page}_${search}_${pageSize}_${false}`;

        // ✅ Save in cache if not cached already
        if (!fromCache) {
          state.cache[cacheKey] = data;
        }
        state.panelUserList = data;
         // ✅ Page handling
         state.currentPage = page;

      // ✅ IMPORTANT: Calculate total pages from count
        const totalCount = data?.pagination?.count || 0;
        state.totalPages = Math.ceil(totalCount / pageSize);

        state.search = search;
        state.pageSize = pageSize;
        state.status = "succeeded";
      })
      .addCase(fetchPanelUser.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.payload;
      })
      // Add Panel User
      .addCase(addPanelUser.pending, (state) => {
        state.status = "loading";
        state.error = null;
      })
      .addCase(addPanelUser.fulfilled, (state) => {
        state.status = "succeeded";
      })
      .addCase(addPanelUser.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.payload;
      })
      // Update Panel User
      .addCase(updatePanelUser.pending, (state) => {
        state.status = "loading";
        state.error = null;
      })
      .addCase(updatePanelUser.fulfilled, (state) => {
        state.status = "succeeded";
      })
      .addCase(updatePanelUser.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.payload;
      });
  },
});

export const { clearError ,  setPage, setPageSize , clearCache} = panelUserSlice.actions;

export default panelUserSlice.reducer;
